import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import posthog from "posthog-js";
import { useNavigate } from "react-router";
import { apiClient, getApiErrorMessage } from "~/lib/api-client";
import type { PaidTrial } from "~/types/BillingTypes";
import type { PitchStatus } from "~/types/PitchTypes";

export interface Team {
  id: number;
  domain: string;
  creator_id: number;
  country: string;
  brand_color?: string;
  brand_name?: string | null;
  brand_aliases?: string[] | null;
  has_brand_guidelines: boolean;
  is_enterprise_plan?: boolean;
  enterprise_config?: Record<string, unknown> | null;
  is_pitch?: boolean;
  pitch_status?: PitchStatus | null;
  is_ecommerce?: boolean;
  is_marketplace?: boolean;
  agency_id?: number | null;
  agency?: AgencyBrand | null;
  role?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Agency {
  id: number;
  name: string;
  website: string | null;
  logo_url?: string | null;
  role: string;
  max_teams: number;
  team_count: number;
  entitlements?: Record<string, unknown>;
  plan_name?: string;
  plan_slug?: string;
  status?: string;
  is_trial?: boolean;
  trial_expired?: boolean;
  trial_days_remaining?: number | null;
  is_max_plan?: boolean;
  can_add_client?: boolean;
  paid_trial?: PaidTrial | null;
}

export type AgencyBrand = Pick<Agency, "id" | "name" | "website" | "logo_url">;

export interface User {
  id?: number;
  gid: string;
  email: string;
  first_name: string;
  last_name?: string;
  picture?: string;
  is_onboarded: boolean;
  team_id?: number;
  is_creating_team?: boolean;
  teams?: Team[];
  agency?: Agency | null;
  pending_agency_signup?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isOnboardingComplete: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  spoofLogin: (token: string) => Promise<void>;
  googleSignIn: (credential: string) => Promise<void>;
  logout: (shouldNavigate?: boolean) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  checkAuthStatus: () => Promise<void>;
  checkOnboardingStatus: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TEAM_STORAGE_KEY = "p_active_team_id";

const getStoredTeamId = (): number | null => {
  try {
    const stored = localStorage.getItem(TEAM_STORAGE_KEY);
    const teamId = stored ? parseInt(stored, 10) : null;
    return teamId;
  } catch (error) {
    console.warn("Failed to read team ID from localStorage:", error);
    return null;
  }
};

const setStoredTeamId = (teamId: number): void => {
  try {
    localStorage.setItem(TEAM_STORAGE_KEY, teamId.toString());
  } catch (error) {
    console.warn("Failed to save team ID to localStorage:", error);
  }
};

const clearStoredTeamId = (): void => {
  try {
    localStorage.removeItem(TEAM_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear team ID from localStorage:", error);
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = !!user;
  const isOnboardingComplete = user?.is_onboarded ?? false;

  const checkOnboardingStatus = () => {
    return user?.is_onboarded ?? false;
  };

  const loadUser = useCallback(async (): Promise<User | null> => {
    try {
      const userResponse = await apiClient.get("/auth/user");

      if (userResponse.status !== 200) {
        return null;
      }

      const userData = userResponse.data?.data?.user || {};

      const teamsResponse = await apiClient.get("/teams");

      if (teamsResponse.status === 200) {
        userData.teams = teamsResponse.data?.data || [];

        const storedTeamId = getStoredTeamId();

        if (
          storedTeamId &&
          userData.teams.some((team: Team) => team.id == storedTeamId)
        ) {
          userData.team_id = storedTeamId;
        } else if (userData.teams.length > 0) {
          const defaultTeam =
            userData.teams.find((team: Team) => !team.is_pitch) ??
            userData.teams[0];
          userData.team_id = defaultTeam.id;
          setStoredTeamId(defaultTeam.id);
        } else {
          clearStoredTeamId();
        }
      } else {
        userData.teams = [];
      }

      apiClient.setUser(userData);
      return userData;
    } catch {
      apiClient.clearLocalState();
      return null;
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const userData = await loadUser();
    setUser(userData);
    setIsLoading(false);
  }, [loadUser]);

  const login = async (credentials: { email: string; password: string }) => {
    const response = await apiClient.post("/auth/login", credentials);

    if (response.status !== 200) {
      throw new Error(response.data?.data?.detail || "Invalid credentials");
    }

    await loadUserDataAndNavigate();
  };

  const spoofLogin = async (token: string) => {
    await apiClient.post(
      "/auth/spoof-login",
      { token },
      { skipAuthRedirect: true }
    );
    await loadUserDataAndNavigate();
  };

  const googleSignIn = async (credential: string) => {
    try {
      await apiClient.post(
        "/auth/google/signin",
        { token: credential },
        { skipAuthRedirect: true }
      );
      await loadUserDataAndNavigate();
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, "Google sign-in failed. Please try again.")
      );
    }
  };

  const loadUserDataAndNavigate = async () => {
    const userResponse = await apiClient.get("/auth/user");

    if (userResponse.status === 200) {
      const userData = userResponse.data?.data?.user || {};

      const teamsResponse = await apiClient.get("/teams");

      if (teamsResponse.status === 200) {
        userData.teams = teamsResponse.data?.data || [];

        const storedTeamId = getStoredTeamId();

        if (
          storedTeamId &&
          userData.teams.some((team: Team) => team.id == storedTeamId)
        ) {
          userData.team_id = storedTeamId;
        } else if (userData.teams.length > 0) {
          const defaultTeam =
            userData.teams.find((team: Team) => !team.is_pitch) ??
            userData.teams[0];
          userData.team_id = defaultTeam.id;
          setStoredTeamId(defaultTeam.id);
        } else {
          clearStoredTeamId();
        }
      } else {
        userData.teams = [];
      }

      setUser(userData);
      apiClient.setUser(userData);
    }
  };

  const logout = async (shouldNavigate: boolean = true) => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.warn("Logout API failed, clearing local state anyway:", error);
      apiClient.clearLocalState();
    }

    setUser(null);
    clearStoredTeamId();
    posthog.reset();

    if (shouldNavigate) {
      navigate("/login");
    }
  };

  const updateUser = (userData: Partial<User>) => {
    setUser((prev) => {
      const updatedUser = prev ? { ...prev, ...userData } : null;
      if (updatedUser) {
        apiClient.setUser(updatedUser);

        if (userData.team_id !== undefined) {
          setStoredTeamId(userData.team_id);
        }

        if (userData.is_onboarded && !prev?.is_onboarded) {
          navigate("/");
        }
      }
      return updatedUser;
    });
  };

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        isOnboardingComplete,
        login,
        spoofLogin,
        googleSignIn,
        logout,
        updateUser,
        checkAuthStatus,
        checkOnboardingStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
